import { type ReactNode } from 'react';

import { cx } from '../lib/cx';

import { IconButton } from './IconButton';
import { Skeleton } from './Skeleton';
import { Tooltip } from './Tooltip';

export interface LegendEntry {
  label: string;
  /** A CSS colour (usually a `var(--cN)` token). */
  color: string;
  /** Optional trailing value (e.g. a percentage). */
  value?: string;
}

export interface ChartCardProps {
  title: string;
  /** Focus-driven help tooltip (replaces the mock's `title`-only `?`). */
  help?: string;
  /** Right-aligned meta (e.g. the volume summary). */
  meta?: ReactNode;
  legend?: readonly LegendEntry[];
  /** Show a shimmer body of this pixel height instead of the chart. */
  loading?: boolean;
  loadingHeight?: number;
  /** Empty-state message shown instead of the chart. */
  empty?: ReactNode;
  /** Expand + export affordances (design-reference §2 chart card wrapper). */
  onExpand?: () => void;
  onExport?: () => void;
  /**
   * "Ask about this" — hands the chart's question to Ask Docsight. The panel is
   * built in Phase 3.7; this only wires the intent, never fakes an answer.
   */
  onAsk?: () => void;
  children?: ReactNode;
  className?: string;
}

/**
 * The chart-card wrapper: title + focus-driven help + optional legend + expand +
 * export + loading/empty handling. Keeps every chart on one consistent chrome.
 */
export function ChartCard({
  title,
  help,
  meta,
  legend,
  loading = false,
  loadingHeight = 210,
  empty,
  onExpand,
  onExport,
  onAsk,
  children,
  className,
}: ChartCardProps): React.JSX.Element {
  return (
    <div className={cx('ds-card', className)} aria-busy={loading || undefined}>
      <div className="ds-card__header">
        <h2 className="ds-card__title">{title}</h2>
        {help !== undefined ? (
          <Tooltip content={help}>
            <button type="button" className="ds-help" aria-label={`About ${title}`}>
              <span aria-hidden="true">?</span>
            </button>
          </Tooltip>
        ) : null}
        <span className="ds-card__meta">{meta}</span>
        {onAsk !== undefined ? (
          <IconButton label={`Ask about ${title}`} tone="bare" onClick={onAsk}>
            <span aria-hidden="true">✦</span>
          </IconButton>
        ) : null}
        {onExport !== undefined ? (
          <IconButton label={`Export ${title}`} tone="bare" onClick={onExport}>
            <span aria-hidden="true">⤓</span>
          </IconButton>
        ) : null}
        {onExpand !== undefined ? (
          <IconButton label={`Expand ${title}`} tone="bare" onClick={onExpand}>
            <span aria-hidden="true">⤢</span>
          </IconButton>
        ) : null}
      </div>
      <div className="ds-card__body">
        {loading ? (
          <Skeleton width="100%" height={loadingHeight} radius={8} />
        ) : empty !== undefined ? (
          <p style={{ color: 'var(--t3)', fontSize: 12.5, textAlign: 'center', padding: '24px 0', margin: 0 }}>
            {empty}
          </p>
        ) : (
          children
        )}
      </div>
      {legend !== undefined && !loading && empty === undefined ? (
        <ul className="ds-chartcard__legend" style={{ listStyle: 'none', margin: 0 }}>
          {legend.map((entry) => (
            <li key={entry.label} className="ds-chartcard__legend-item">
              <span className="ds-chartcard__swatch" aria-hidden="true" style={{ background: entry.color }} />
              {entry.label}
              {entry.value !== undefined ? (
                <strong style={{ color: 'var(--t1)', fontVariantNumeric: 'tabular-nums' }}> {entry.value}</strong>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
